/*

  RightSideTabs.ts

  Desktop tabbed container for Performance/Stats/Leaderboard in Gáta Dagsins

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjálmur Þorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { ComponentFunc, m } from "./mithril";
import { IPlayerMove, IView } from "./types";
import { TabBar, ITab } from "./tabbar";
import { Thermometer } from "./thermometer";
import { StatsView } from "./statsview";
import { LeaderboardView } from "./leaderboardview";
import { ts } from "./i18n";

interface IRightSideTabsAttributes {
  view: IView;
  selectedMoves: IPlayerMove[];
  bestMove: IPlayerMove | undefined;
  onMoveClick: (word: string, coord: string) => void;
}

export const RightSideTabs: ComponentFunc<IRightSideTabsAttributes> = () => {
  // Component-local state for active tab (defaults to performance)
  let activeTab: string = "performance";

  return {
    view: (vnode) => {
      const { view, selectedMoves, bestMove, onMoveClick } = vnode.attrs;
      const { riddle, state, leaderboard } = view.model;

      if (!riddle) {
        return m(".gatadagsins-right-side-tabs", "");
      }

      // Check if current user is on the leaderboard
      const currentUserId = state?.netskraflUserId || "";
      const isOnLeaderboard = leaderboard && leaderboard.some(
        entry => entry.userId === currentUserId
      );

      const tabs: ITab[] = [
        { id: "performance", label: ts("Frammistaða"), iconGlyph: "dashboard" },
        { id: "stats", label: ts("Tölfræði"), iconGlyph: "stats" },
        {
          id: "leaderboard",
          label: ts("Stigatafla"),
          iconGlyph: "tower",
          badgeGlyph: isOnLeaderboard ? "star" : undefined
        }
      ];

      const handleTabChange = (tabId: string) => {
        activeTab = tabId;
      };

      return m(".gatadagsins-right-side-tabs", [
        // Tab navigation
        m(TabBar, {
          tabs,
          activeTab,
          onTabChange: handleTabChange
        }),

        // Tab content
        m(".tab-content", [
          // Performance tab (thermometer)
          activeTab === "performance" ? m(Thermometer, {
            riddle,
            selectedMoves,
            bestMove,
            onMoveClick
          }) : null,

          // Stats tab
          activeTab === "stats" ? m(StatsView, {
            stats: view.model.userStats || null,
            loading: false
          }) : null,

          // Leaderboard tab
          activeTab === "leaderboard" ? m(LeaderboardView, {
            leaderboard: view.model.leaderboard || [],
            currentUserId: state?.netskraflUserId || "",
            date: riddle.date,
            loading: false
          }) : null
        ])
      ]);
    }
  };
};
